import { ArrowUpRight, ArrowDownRight, Target, TrendingUp } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import type { DailyRevenue } from '@/hooks/useDashboardPremium'

interface DashboardHeroSectionProps {
  confirmedRevenue: number
  monthlyGoal: number
  goalProgress: number
  previousMonthRevenue: number
  revenueGrowthPercent: number
  dailyRevenueChart: DailyRevenue[]
}

const formatCurrency = (value: number) => 
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatCurrencyShort = (value: number) => {
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1)}k`
  }
  return formatCurrency(value)
}

export function DashboardHeroSection({
  confirmedRevenue,
  monthlyGoal,
  goalProgress,
  previousMonthRevenue,
  revenueGrowthPercent,
  dailyRevenueChart
}: DashboardHeroSectionProps) {
  const remaining = Math.max(0, monthlyGoal - confirmedRevenue)
  const isPositiveGrowth = revenueGrowthPercent >= 0

  // Compute cumulative data for the chart
  let cumulativeCurrent = 0
  let cumulativePrevious = 0
  const cumulativeData = dailyRevenueChart.map(d => {
    cumulativeCurrent += d.revenue
    cumulativePrevious += d.previousMonthRevenue
    return {
      ...d,
      cumulativeRevenue: cumulativeCurrent,
      cumulativePrevious: cumulativePrevious
    }
  })

  return (
    <section className="space-y-4">
      {/* Header with comparison badge */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Performance do Mês
        </h2>
        {previousMonthRevenue > 0 && (
          <Badge 
            variant="outline" 
            className={cn(
              "text-xs font-medium",
              isPositiveGrowth ? "text-success border-success/30" : "text-destructive border-destructive/30"
            )}
          >
            {isPositiveGrowth ? (
              <ArrowUpRight className="h-3 w-3 mr-1" />
            ) : (
              <ArrowDownRight className="h-3 w-3 mr-1" />
            )}
            {isPositiveGrowth ? '+' : ''}{revenueGrowthPercent.toFixed(1)}% vs mês anterior
          </Badge>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left: Main revenue metrics */}
        <Card className="shadow-card lg:col-span-1">
          <CardContent className="p-6 space-y-6">
            {/* Confirmed Revenue */}
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Receita Confirmada</p>
              <p className="text-4xl font-display font-bold text-primary tracking-tight">
                {formatCurrency(confirmedRevenue)}
              </p>
            </div>

            {/* Goal Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Meta: {formatCurrency(monthlyGoal)}</span>
                <span className={cn(
                  "font-semibold",
                  goalProgress >= 100 ? "text-success" : goalProgress >= 70 ? "text-primary" : "text-warning"
                )}>
                  {goalProgress.toFixed(0)}%
                </span>
              </div>
              <Progress 
                value={goalProgress} 
                className="h-3"
              />
              {remaining > 0 && (
                <p className="text-xs text-muted-foreground">
                  Faltam <span className="font-medium text-foreground">{formatCurrency(remaining)}</span> para atingir a meta
                </p>
              )}
            </div>

            {/* Previous month comparison */}
            {previousMonthRevenue > 0 && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Mês anterior</span>
                  <span className="text-sm font-medium">{formatCurrency(previousMonthRevenue)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-muted-foreground">Diferença</span>
                  <span className={cn(
                    "text-sm font-medium flex items-center gap-1",
                    isPositiveGrowth ? "text-success" : "text-destructive"
                  )}>
                    {isPositiveGrowth ? <TrendingUp className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                    {formatCurrency(Math.abs(confirmedRevenue - previousMonthRevenue))}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Revenue chart */}
        <Card className="shadow-card lg:col-span-2">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-muted-foreground mb-4">
              Receita Acumulada (comparativo mensal)
            </p>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cumulativeData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => formatCurrencyShort(value)}
                    width={65}
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatCurrency(value), '']}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      boxShadow: 'var(--shadow-lg)'
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={30}
                    formatter={(value) => (
                      <span className="text-xs text-muted-foreground">{value}</span>
                    )}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cumulativeRevenue" 
                    name="Este mês"
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cumulativePrevious" 
                    name="Mês anterior"
                    stroke="hsl(var(--muted-foreground))" 
                    strokeWidth={1.5}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={{ r: 3, fill: 'hsl(var(--muted-foreground))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
